import useUiTranslation from "../utils/useUiTranslation";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { printWarehouseSupplyRequest } from '../api/warehouseSupply';
import usePageTranslation from '../utils/usePageTranslation';
import { extractItems } from '../utils/itemUtils';
import { matchesSearchTokens } from '../utils/search';

const statusClasses = {
  approved: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  rejected: 'bg-red-100 text-red-700',
  completed: 'bg-blue-100 text-blue-700',
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (err) {
    return value;
  }
};

const WarehouseSupplyRequestsPage = () => {
  const uiText = useUiTranslation();
  const tr = usePageTranslation('warehouseSupplyRequests');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    subCategory: 'all',
    department: 'all',
    section: 'all',
    status: 'all',
    fromDate: '',
    toDate: '',
    itemSearch: '',
  });
  const [expandedRequestId, setExpandedRequestId] = useState(null);
  const [itemsCache, setItemsCache] = useState({});
  const [itemsError, setItemsError] = useState({});
  const [itemsLoadingId, setItemsLoadingId] = useState(null);
  const [closingRequestId, setClosingRequestId] = useState(null);
  const navigate = useNavigate();
  const [sortOption, setSortOption] = useState('newest');
  const [printingId, setPrintingId] = useState(null);

  const normalizeItems = useCallback((items = []) =>
    items.map((item) => ({
      ...item,
      item_id: item.item_id || item.id,
      item_name: item.item_name || item.name,
      quantity: Number(item.quantity ?? item.requested_quantity ?? 0),
      supplied_quantity: Number(item.supplied_quantity ?? item.supplied ?? 0),
    })), []);

  const getFulfillmentStats = useCallback((request) => {
    const items = itemsCache[request.id] || normalizeItems(request.items || []);
    const requestedTotal = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const suppliedTotal = items.reduce(
      (sum, item) => sum + Number(item.supplied_quantity || 0),
      0,
    );
    const outstanding = Math.max(requestedTotal - suppliedTotal, 0);
    const progress = requestedTotal
      ? Math.min(100, Math.round((suppliedTotal / requestedTotal) * 100))
      : 0;
    const fullySupplied =
      items.length > 0 &&
      items.every((item) => Number(item.supplied_quantity || 0) >= Number(item.quantity || 0));

    return { requestedTotal, suppliedTotal, outstanding, progress, fullySupplied };
  }, [itemsCache, normalizeItems]);
  const getStatusLabel = useCallback((status) => {
    if (!status) {
      return tr('statuses.unknown', 'Unknown');
    }
    return tr(`statuses.${status.toLowerCase()}`, status);
  }, [tr]);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/warehouse-supply');
      const data = res.data || [];
      setRequests(data);
      setItemsCache((prev) => {
        const next = { ...prev };
        data.forEach((request) => {
          if (request.items && request.items.length > 0) {
            next[request.id] = normalizeItems(request.items);
          }
        });
        return next;
      });
    } catch (err) {
      console.error('Failed to load requests:', err);
      setError(
        tr('alerts.loadFailed', 'Failed to load warehouse supply requests. Please try again.'),
      );
    } finally {
      setLoading(false);
    }
  }, [normalizeItems, tr]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const closeRequest = async (requestId) => {
    const confirmMessage = tr(
      'requestCard.closeRequestConfirm',
      'Close this warehouse supply request even if it is not fully supplied?',
    );

    if (!window.confirm(confirmMessage)) return;

    try {
      setClosingRequestId(requestId);
      const res = await api.post(`/warehouse-supply/${requestId}/close`);

      setRequests((prev) =>
        prev.map((req) =>
          req.id === requestId
            ? {
                ...req,
                status: res.data?.status || 'Completed',
              }
            : req,
        ),
      );

      navigate('/request-submitted', {
        state: {
          title: tr('completionScreen.title', 'Request Action Completed Successfully!'),
          message: tr('completionScreen.message', 'Warehouse supply request closed successfully.'),
          requestId,
          requestType: tr('completionScreen.requestType', 'Warehouse Supply'),
          statusMessage: res.data?.message || tr('completionScreen.message', 'Warehouse supply request closed successfully.'),
          nextApprover: tr('completionScreen.none', 'N/A'),
          pendingLevel: tr('completionScreen.none', 'N/A'),
        },
      });
    } catch (err) {
      console.error('Failed to close request:', err);
      alert(
        err.response?.data?.message ||
          tr('alerts.closeFailed', 'Failed to close the request. Please try again.'),
      );
    } finally {
      setClosingRequestId(null);
    }
  };

  const handleFilterChange = (key) => (event) => {
    const value = event.target.value;
    setFilters((prev) => {
      if (key === 'category') {
        return { ...prev, category: value, subCategory: 'all' };
      }
      return { ...prev, [key]: value };
    });
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: 'all',
      subCategory: 'all',
      department: 'all',
      section: 'all',
      status: 'all',
      fromDate: '',
      toDate: '',
      itemSearch: '',
    });
  };

  const activeFilters = useMemo(() => {
    const chips = [];

    if (filters.search.trim()) {
      chips.push({
        key: 'search',
        label: tr('activeFilters.search', 'Search: "{{value}}"', {
          value: filters.search.trim(),
        }),
      });
    }

    if (filters.department !== 'all') {
      chips.push({
        key: 'department',
        label: tr('activeFilters.department', 'Department: {{value}}', {
          value: filters.department,
        }),
      });
    }
    if (filters.category !== 'all') {
      chips.push({
        key: 'category',
        label: tr('activeFilters.category', 'Category: {{value}}', {
          value: filters.category,
        }),
      });
    }

    if (filters.subCategory !== 'all') {
      chips.push({
        key: 'subCategory',
        label: tr('activeFilters.subCategory', 'Sub Category: {{value}}', {
          value: filters.subCategory,
        }),
      });
    }

    if (filters.section !== 'all') {
      chips.push({
        key: 'section',
        label: tr('activeFilters.section', 'Section: {{value}}', {
          value: filters.section,
        }),
      });
    }

    if (filters.status !== 'all') {
      chips.push({
        key: 'status',
        label: tr('activeFilters.status', 'Status: {{value}}', {
          value: getStatusLabel(filters.status),
        }),
      });
    }

    if (filters.fromDate) {
      chips.push({
        key: 'fromDate',
        label: tr('activeFilters.from', 'From: {{value}}', {
          value: filters.fromDate,
        }),
      });
    }

    if (filters.toDate) {
      chips.push({
        key: 'toDate',
        label: tr('activeFilters.to', 'To: {{value}}', {
          value: filters.toDate,
        }),
      });
    }

    if (filters.itemSearch.trim()) {
      chips.push({
        key: 'itemSearch',
        label: tr('activeFilters.itemSearch', 'Item search: "{{value}}"', {
          value: filters.itemSearch.trim(),
        }),
      });
    }

    return chips;
  }, [filters, getStatusLabel, tr]);

  const departments = useMemo(
    () => Array.from(new Set(requests.map((req) => req.department_name).filter(Boolean))).sort(),
    [requests],
  );

  const sections = useMemo(
    () => Array.from(new Set(requests.map((req) => req.section_name).filter(Boolean))).sort(),
    [requests],
  );

  const statuses = useMemo(
    () => Array.from(new Set(requests.map((req) => (req.status || '').trim()).filter(Boolean))).sort(),
    [requests],
  );

  const categories = useMemo(() => {
    const set = new Set();
    requests.forEach((req) => {
      const requestItems = itemsCache[req.id] || normalizeItems(req.items || []);
      requestItems.forEach((item) => {
        const category = item.category || item.item_category;
        if (category) set.add(String(category).trim());
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [requests, itemsCache, normalizeItems]);

  const subCategories = useMemo(() => {
    const set = new Set();
    requests.forEach((req) => {
      const requestItems = itemsCache[req.id] || normalizeItems(req.items || []);
      requestItems.forEach((item) => {
        const category = String(item.category || item.item_category || '').trim();
        const sub = String(item.sub_category || item.subcategory || '').trim();
        if (!sub) return;
        if (filters.category !== 'all' && category !== filters.category) return;
        set.add(sub);
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [requests, itemsCache, filters.category, normalizeItems]);

  const filteredRequests = useMemo(() => {
    const normalizedSearch = filters.search.trim().toLowerCase();
    const hasItemSearch = Boolean(filters.itemSearch.trim());
    const from = filters.fromDate ? new Date(filters.fromDate) : null;
    const to = filters.toDate ? new Date(filters.toDate) : null;

    if (from) {
      from.setHours(0, 0, 0, 0);
    }

    if (to) {
      to.setHours(23, 59, 59, 999);
    }

    return requests.filter((req) => {
      const createdAt = req.created_at ? new Date(req.created_at) : null;

      if (from && createdAt && createdAt < from) {
        return false;
      }

      if (to && createdAt && createdAt > to) {
        return false;
      }

      if (filters.department !== 'all' && req.department_name !== filters.department) {
        return false;
      }

      if (filters.section !== 'all' && req.section_name !== filters.section) {
        return false;
      }

      if (filters.status !== 'all' && (req.status || '') !== filters.status) {
        return false;
      }

      const requestItems = itemsCache[req.id] || normalizeItems(req.items || []);

      if (filters.category !== 'all') {
        const hasCategory = requestItems.some(
          (item) => String(item.category || item.item_category || '').trim() === filters.category,
        );
        if (!hasCategory) return false;
      }

      if (filters.subCategory !== 'all') {
        const hasSubCategory = requestItems.some((item) => {
          const category = String(item.category || item.item_category || '').trim();
          const sub = String(item.sub_category || item.subcategory || '').trim();
          if (!sub) return false;
          if (filters.category !== 'all' && category !== filters.category) return false;
          return sub === filters.subCategory;
        });
        if (!hasSubCategory) return false;
      }

      if (hasItemSearch) {
        const itemHaystack = requestItems.flatMap((item) => [item.item_id, item.item_name]);

        if (!matchesSearchTokens(filters.itemSearch, itemHaystack)) {
          return false;
        }
      }

      if (normalizedSearch) {
        const haystack = [
          req.id,
          req.department_name,
          req.section_name,
          req.justification,
        ]
          .filter(Boolean)
          .map((value) => String(value).toLowerCase())
          .join(' ');

        if (!haystack.includes(normalizedSearch)) {
          return false;
        }
      }

      return true;
    });
  }, [filters, requests, itemsCache, normalizeItems]);

  const sortRequests = useCallback((list) => {
    const sorted = [...list];

    return sorted.sort((a, b) => {
      if (sortOption === 'newest') {
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }

      if (sortOption === 'oldest') {
        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      }

      const statsA = getFulfillmentStats(a);
      const statsB = getFulfillmentStats(b);

      if (sortOption === 'outstanding') {
        return statsB.outstanding - statsA.outstanding;
      }

      if (sortOption === 'progress') {
        return statsB.progress - statsA.progress;
      }

      return 0;
    });
  }, [getFulfillmentStats, sortOption]);

  const sortedRequests = useMemo(
    () => sortRequests(filteredRequests),
    [filteredRequests, sortRequests],
  );

  const summary = useMemo(() => {
    const totals = {
      total: requests.length,
      byStatus: {},
    };

    requests.forEach((req) => {
      const key = (req.status || 'Unknown').trim() || 'Unknown';
      totals.byStatus[key] = (totals.byStatus[key] || 0) + 1;
    });

    return totals;
  }, [requests]);

  const toggleItems = async (requestId) => {
    if (expandedRequestId === requestId) {
      setExpandedRequestId(null);
      return;
    }

    if (!itemsCache[requestId] && !itemsError[requestId]) {
      try {
        setItemsLoadingId(requestId);
        const res = await api.get(`/requests/${requestId}/items`);
        setItemsCache((prev) => ({
          ...prev,
          [requestId]: normalizeItems(extractItems(res.data)),
        }));
      } catch (err) {
        console.error('Failed to load requested items:', err);
        setItemsError((prev) => ({
          ...prev,
          [requestId]: tr(
            'alerts.itemsLoadFailed',
            'Failed to load requested items. Please try again later.',
          ),
        }));
      } finally {
        setItemsLoadingId(null);
      }
    }

    setExpandedRequestId(requestId);
  };

  const renderStatusBadge = (status) => {
    if (!status) {
      return (
        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
          {tr('statuses.unknown', 'Unknown')}
        </span>
      );
    }

    const key = status.toLowerCase();
    const badgeClass = statusClasses[key] || 'bg-gray-100 text-gray-600';
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badgeClass}`}>
        {getStatusLabel(status)}
      </span>
    );
  };

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const handlePrint = async (requestId) => {
    setPrintingId(requestId);
    try {
      const data = await printWarehouseSupplyRequest(requestId);
      const { request, items = [], print_count: printCount } = data;

      const popup = window.open('', '_blank');
      if (!popup) {
        alert(tr('alerts.enablePopups', 'Please enable popups to print this request.'));
        return;
      }

      const formattedCreatedAt = formatDateTime(request?.created_at);
      const rowsHtml = items
        .map(
          (item, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>${escapeHtml(item.item_name)}</td>
            <td>${escapeHtml(item.quantity)}</td>
            <td>${escapeHtml(item.supplied_quantity)}</td>
            <td>${escapeHtml(Math.max(Number(item.quantity || 0) - Number(item.supplied_quantity || 0), 0))}</td>
          </tr>
        `,
        )
        .join('');

      popup.document.write(`<!doctype html>
        <html lang="en">
          <head>
            <meta charset="utf-8" />
            <title>${tr('print.title', 'Warehouse Supply Request')} #${escapeHtml(requestId)}</title>
            <style>
              body { font-family: 'Inter', system-ui, -apple-system, sans-serif; color: #1f2937; padding: 24px; }
              h1 { font-size: 24px; margin-bottom: 4px; }
              h2 { font-size: 18px; margin: 24px 0 8px; }
              .meta { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 8px; margin: 12px 0; }
              .meta div { padding: 8px 10px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; }
              .meta span { display: block; font-size: 12px; color: #6b7280; margin-bottom: 4px; }
              table { width: 100%; border-collapse: collapse; margin-top: 16px; font-size: 14px; }
              th, td { border: 1px solid #e5e7eb; padding: 8px; text-align: left; }
              th { background: #f3f4f6; font-weight: 600; }
              tfoot td { font-weight: 700; background: #f9fafb; }
              .badge { display: inline-block; background: #eff6ff; color: #1d4ed8; padding: 4px 10px; border-radius: 9999px; font-size: 12px; margin-top: 8px; }
              @media print { body { padding: 0; } .no-print { display: none; } }
            </style>
          </head>
          <body>
            <div class="no-print" style="display: flex; justify-content: flex-end; margin-bottom: 12px;">
              <button onclick="window.print()" style="padding: 8px 12px; background: #2563eb; color: white; border: none; border-radius: 6px; cursor: pointer;">${tr('actions.print', 'Print')}</button>
            </div>
            <h1>${tr('print.heading', 'Warehouse Supply Request')} #${escapeHtml(requestId)}</h1>
            <div class="badge">${tr('print.printCount', 'Print count')}: ${escapeHtml(printCount)}</div>
            <div class="meta">
              <div><span>${tr('requestCard.department', 'Department')}</span>${escapeHtml(request?.department_name || '—')}</div>
              <div><span>${tr('requestCard.section', 'Section')}</span>${escapeHtml(request?.section_name || '—')}</div>
              <div><span>${tr('requestCard.warehouse', 'Warehouse')}</span>${escapeHtml(request?.warehouse_name || '—')}</div>
              <div><span>${tr('requestCard.submitted', 'Submitted')}</span>${escapeHtml(formattedCreatedAt)}</div>
              <div><span>${tr('print.requester', 'Requester')}</span>${escapeHtml(request?.requester_name || '—')}</div>
              <div><span>${tr('print.role', 'Role')}</span>${escapeHtml(request?.requester_role || '—')}</div>
              <div><span>${tr('requestCard.status', 'Status')}</span>${escapeHtml(request?.status || '—')}</div>
            </div>
            <h2>${tr('requestCard.justification', 'Justification')}</h2>
            <p style="white-space: pre-line; background: #f9fafb; border: 1px solid #e5e7eb; padding: 12px; border-radius: 6px;">${escapeHtml(
              request?.justification || tr('requestCard.noJustification', 'No justification provided.'),
            )}</p>
            <h2>${tr('print.items', 'Requested items')}</h2>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>${tr('print.itemName', 'Item')}</th>
                  <th>${tr('items.requestedQuantity', 'Requested: {{quantity}}', { quantity: '' }).replace(': ', '')}</th>
                  <th>${tr('items.suppliedQuantity', 'Supplied: {{quantity}}', { quantity: '' }).replace(': ', '')}</th>
                  <th>${tr('print.outstanding', 'Outstanding')}</th>
                </tr>
              </thead>
              <tbody>${rowsHtml || `<tr><td colspan="5">${tr('items.empty', 'No requested items available.')}</td></tr>`}</tbody>
              <tfoot>
                <tr>
                  <td colspan="2">${tr('print.totals', 'Totals')}</td>
                  <td>${items.reduce((sum, item) => sum + Number(item.quantity || 0), 0)}</td>
                  <td>${items.reduce((sum, item) => sum + Number(item.supplied_quantity || 0), 0)}</td>
                  <td>${items.reduce(
                    (sum, item) => sum + Math.max(Number(item.quantity || 0) - Number(item.supplied_quantity || 0), 0),
                    0,
                  )}</td>
                </tr>
              </tfoot>
            </table>
            <p style="margin-top: 16px; font-size: 12px; color: #6b7280;">${tr(
              'print.footer',
              'Generated for warehouse fulfillment tracking.',
            )}</p>
          </body>
        </html>`);
      popup.document.close();
      popup.focus();
      popup.print();
    } catch (err) {
      console.error('Failed to print warehouse supply request:', err);
      alert(tr('alerts.printFailed', 'Failed to print warehouse supply request.'));
    } finally {
      setPrintingId(null);
    }
  };

  return (
    <>
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold">{tr('title', 'Warehouse Supply Requests')}</h1>
            <p className="text-gray-600">{tr('subtitle', 'Review approved warehouse requests and record supplied items.')}</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={fetchRequests}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              {tr('actions.refresh', 'Refresh')}
            </button>
            <button
              type="button"
              onClick={clearFilters}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              {tr('actions.clearFilters', 'Clear Filters')}
            </button>
          </div>
        </div>

        {error && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded border bg-white p-4 shadow-sm">
            <p className="text-sm text-gray-500">{tr('summary.total', 'Total Requests')}</p>
            <p className="mt-1 text-2xl font-semibold">{summary.total}</p>
          </div>
          {Object.entries(summary.byStatus).map(([status, count]) => (
            <div key={status} className="rounded border bg-white p-4 shadow-sm">
              <p className="text-sm text-gray-500">{getStatusLabel(status)}</p>
              <p className="mt-1 text-2xl font-semibold">{count}</p>
            </div>
          ))}
        </section>

        <section className="rounded border bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <h2 className="text-lg font-semibold">{tr('filters.title', 'Filters')}</h2>
            <div className="flex flex-col gap-2 text-sm md:flex-row md:items-center">
              <label className="font-medium text-gray-700" htmlFor="sort">
                {tr('sort.label', 'Sort by')}
              </label>
              <select
                id="sort"
                value={sortOption}
                onChange={(event) => setSortOption(event.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none md:w-56"
              >
                <option value="newest">{tr('sort.options.newest', 'Newest')}</option>
                <option value="oldest">{tr('sort.options.oldest', 'Oldest')}</option>
                <option value="outstanding">
                  {tr('sort.options.outstanding', 'Outstanding items')}
                </option>
                <option value="progress">{tr('sort.options.progress', 'Highest progress')}</option>
              </select>
            </div>
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700" htmlFor="search">
                {tr('filters.searchLabel', 'Search')}
              </label>
              <input
                id="search"
                type="text"
                placeholder={tr(
                  'filters.searchPlaceholder',
                  'Search by ID, department, section or justification',
                )}
                value={filters.search}
                onChange={handleFilterChange('search')}
                className="w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700" htmlFor="category">
                {tr('filters.category', 'Category')}
              </label>
              <select
                id="category"
                value={filters.category}
                onChange={handleFilterChange('category')}
                className="w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
              >
                <option value="all">{tr('filters.allCategories', 'All Categories')}</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700" htmlFor="subCategory">
                {tr('filters.subCategory', 'Sub Category')}
              </label>
              <select
                id="subCategory"
                value={filters.subCategory}
                onChange={handleFilterChange('subCategory')}
                className="w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
              >
                <option value="all">{tr('filters.allSubCategories', 'All Sub Categories')}</option>
                {subCategories.map((subCategory) => (
                  <option key={subCategory} value={subCategory}>
                    {subCategory}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700" htmlFor="itemSearch">
                {tr('filters.itemSearch', 'Item search')}
              </label>
              <input
                id="itemSearch"
                type="search"
                placeholder={tr('filters.itemSearchPlaceholder', 'Type to filter items')}
                value={filters.itemSearch}
                onChange={handleFilterChange('itemSearch')}
                className="w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700" htmlFor="department">
                {tr('filters.department', 'Department')}
              </label>
              <select
                id="department"
                value={filters.department}
                onChange={handleFilterChange('department')}
                className="w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
              >
                <option value="all">{tr('filters.allOption', 'All')}</option>
                {departments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700" htmlFor="section">
                {tr('filters.section', 'Section')}
              </label>
              <select
                id="section"
                value={filters.section}
                onChange={handleFilterChange('section')}
                className="w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
              >
                <option value="all">{tr('filters.allOption', 'All')}</option>
                {sections.map((section) => (
                  <option key={section} value={section}>
                    {section}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700" htmlFor="status">
                {tr('filters.status', 'Status')}
              </label>
              <select
                id="status"
                value={filters.status}
                onChange={handleFilterChange('status')}
                className="w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
              >
                <option value="all">{tr('filters.allOption', 'All')}</option>
                {statuses.map((status) => (
                  <option key={status} value={status}>
                    {getStatusLabel(status)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700" htmlFor="fromDate">
                {tr('filters.from', 'From')}
              </label>
              <input
                id="fromDate"
                type="date"
                value={filters.fromDate}
                onChange={handleFilterChange('fromDate')}
                className="w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700" htmlFor="toDate">
                {tr('filters.to', 'To')}
              </label>
              <input
                id="toDate"
                type="date"
                value={filters.toDate}
                onChange={handleFilterChange('toDate')}
                className="w-full rounded border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          <div className="mt-4 rounded border border-dashed border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
            <p className="font-medium text-gray-900">{tr('activeFilters.title', 'Active filters')}</p>
            {activeFilters.length === 0 ? (
              <p className="mt-1 text-gray-600">{tr('activeFilters.none', 'No filters applied.')}</p>
            ) : (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {activeFilters.map((filter) => (
                  <span
                    key={filter.key}
                    className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-medium text-gray-800 shadow-sm"
                  >
                    {filter.label}
                  </span>
                ))}
                <button
                  type="button"
                  onClick={clearFilters}
                  className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                >
                  {tr('activeFilters.clear', 'Clear all')}
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="space-y-4">
          {loading ? (
            <p className="text-gray-600">{tr('list.loading', 'Loading requests…')}</p>
          ) : sortedRequests.length === 0 ? (
            <p className="rounded border border-gray-200 bg-white p-6 text-center text-gray-600">
              {tr('list.empty', 'No warehouse supply requests match the selected filters.')}
            </p>
          ) : (
            sortedRequests.map((req) => {
              const items = itemsCache[req.id] || normalizeItems(req.items || []);
              const stats = getFulfillmentStats({ ...req, items });
              const isCompleted = (req.status || '').toLowerCase() === 'completed';

              return (
                <article key={req.id} className="rounded border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-gray-500">{tr('requestCard.requestId', 'Request ID')}</p>
                      <p className="text-xl font-semibold">{req.id}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                      <div>
                        <span className="font-medium text-gray-700">{tr('requestCard.department', 'Department')}:</span>{' '}
                        {req.department_name || '—'}
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">{tr('requestCard.section', 'Section')}:</span>{' '}
                        {req.section_name || '—'}
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">{tr('requestCard.warehouse', 'Warehouse')}:</span>{' '}
                        {req.warehouse_name || '—'}
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">{tr('requestCard.submitted', 'Submitted')}:</span>{' '}
                        {formatDateTime(req.created_at)}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700">{tr('requestCard.status', 'Status')}:</span>
                        {renderStatusBadge(req.status)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 rounded bg-gray-50 p-4 text-sm text-gray-700">
                    <p className="font-medium text-gray-900">{tr('requestCard.justification', 'Justification')}</p>
                    <p className="mt-1 whitespace-pre-line">
                      {req.justification || tr('requestCard.noJustification', 'No justification provided.')}
                    </p>
                  </div>

                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    <div className="rounded border border-gray-200 bg-white p-3 shadow-sm">
                      <p className="text-sm text-gray-500">{tr('requestCard.itemsCount', 'Items in request')}</p>
                      <p className="mt-1 text-xl font-semibold">{items.length}</p>
                    </div>
                    <div className="rounded border border-gray-200 bg-white p-3 shadow-sm">
                      <p className="text-sm text-gray-500">{tr('requestCard.supplied', 'Supplied so far')}</p>
                      <p className="mt-1 text-xl font-semibold">{stats.suppliedTotal}</p>
                    </div>
                    <div className="rounded border border-gray-200 bg-white p-3 shadow-sm">
                      <p className="text-sm text-gray-500">{tr('requestCard.outstanding', 'Outstanding')}</p>
                      <p className={`mt-1 text-xl font-semibold ${stats.outstanding > 0 ? 'text-amber-600' : 'text-green-700'}`}>
                        {stats.outstanding}
                      </p>
                      <p className="text-xs text-gray-600">
                        {stats.outstanding > 0
                          ? tr(
                              'requestCard.outstandingDetail',
                              '{{count}} items still need to be supplied.',
                              {
                                count: stats.outstanding,
                              },
                            )
                          : tr('requestCard.noOutstanding', 'Everything requested has been supplied.')}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-sm font-medium text-gray-800">
                        {tr('requestCard.progressLabel', '{{progress}}% fulfilled', {
                          progress: stats.progress,
                        })}
                      </p>
                      <p className="text-sm text-gray-600">
                        {tr('requestCard.suppliedSummary', '{{supplied}} of {{requested}} supplied', {
                          supplied: stats.suppliedTotal,
                          requested: stats.requestedTotal || '—',
                        })}
                      </p>
                    </div>
                    <div className="h-2 w-full rounded-full bg-gray-200">
                      <div
                        className={`h-2 rounded-full transition-all ${stats.fullySupplied ? 'bg-green-500' : 'bg-blue-500'}`}
                        style={{ width: `${stats.progress}%` }}
                      />
                    </div>
                    <p className="text-sm text-gray-600">
                      {stats.fullySupplied
                        ? tr('requestCard.fullySupplied', 'All requested quantities have been supplied.')
                        : tr(
                          'requestCard.fulfillmentHint',
                          'Record supplied quantities or close the request when you are done.',
                        )}
                    </p>
                  </div>

                  <div className="mt-4 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => navigate(`/warehouse-supply/${req.id}`)}
                      className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={isCompleted}
                    >
                      {tr('requestCard.recordSupplied', 'Record Supplied Items')}
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePrint(req.id)}
                      disabled={printingId === req.id}
                      className="rounded border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-800 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {printingId === req.id
                        ? tr('actions.printing', 'Printing…')
                        : tr('actions.print', 'Print')}
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleItems(req.id)}
                      className="rounded border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100"
                    >
                      {expandedRequestId === req.id
                        ? tr('requestCard.hideItems', 'Hide Requested Items')
                        : tr('requestCard.viewItems', 'View Requested Items')}
                    </button>
                    <button
                      type="button"
                      onClick={() => closeRequest(req.id)}
                      disabled={isCompleted || closingRequestId === req.id}
                      className={`rounded border px-4 py-2 text-sm font-medium ${
                        isCompleted
                          ? 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-500'
                          : 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
                      }`}
                    >
                      {closingRequestId === req.id
                        ? tr('requestCard.closing', 'Closing…')
                        : tr('requestCard.closeRequest', 'Close Request')}
                    </button>
                    <span
                      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
                        isCompleted
                          ? 'bg-gray-100 text-gray-700'
                          : stats.outstanding > 0
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-blue-50 text-blue-700'
                      }`}
                    >
                      {isCompleted
                        ? tr('requestCard.badges.closed', 'Closed')
                        : stats.outstanding > 0
                          ? tr('requestCard.badges.needsAttention', 'Needs supply attention')
                          : tr('requestCard.badges.inProgress', 'In progress')}
                    </span>
                  </div>

                  {expandedRequestId === req.id && (
                    <div className="mt-4 rounded border border-gray-200 bg-gray-50 p-4">
                      {itemsLoadingId === req.id ? (
                        <p className="text-sm text-gray-600">{tr('items.loading', 'Loading requested items…')}</p>
                      ) : itemsError[req.id] ? (
                        <p className="text-sm text-red-600">{itemsError[req.id]}</p>
                      ) : items && items.length > 0 ? (
                        <ul className="space-y-2 text-sm text-gray-700">
                          {items.map((item) => (
                            <li
                              key={item.item_id || item.id}
                              className="rounded bg-white px-3 py-2 shadow"
                            >
                              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                  <p className="font-medium text-gray-900">{item.item_name}</p>
                                  <p className="text-xs text-gray-500">{uiText("ID:")}{" "}{item.item_id || item.id}</p>
                                </div>
                                <div className="flex flex-wrap gap-3 text-sm">
                                  <span className="text-gray-700">
                                    {tr('items.requestedQuantity', 'Requested: {{quantity}}', {
                                      quantity: item.quantity,
                                    })}
                                  </span>
                                  <span className="rounded-full bg-blue-50 px-3 py-1 text-blue-700">
                                    {tr('items.suppliedQuantity', 'Supplied: {{quantity}}', {
                                      quantity: item.supplied_quantity || 0,
                                    })}
                                  </span>
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-600">{tr('items.empty', 'No requested items available.')}</p>
                      )}
                    </div>
                  )}
                </article>
              );
            })
          )}
        </section>
      </div>
    </>
  );
};

export default WarehouseSupplyRequestsPage;